/** Small formatting helpers and provenance labels (port of the prototype's ui.js). */
import type { BuildModel, FieldStatus, Part } from '@/domain';
import { fmtMm } from '@/domain';

const dateFmt = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
const timeFmt = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit' });

export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '';
  try { return dateFmt.format(new Date(iso)); } catch { return ''; }
}
export function fmtTime(iso: string | null | undefined): string {
  if (!iso) return '';
  try { return timeFmt.format(new Date(iso)); } catch { return ''; }
}
export const mmText = (ticks: number): string => fmtMm(ticks);
/** Hashes are always shown as their first eight hex characters (ui-spec §5 item 4). */
export const short = (hash: string | null | undefined): string => (hash || '').slice(0, 8);
export const plural = (n: number, one: string, many = `${one}s`): string => `${n} ${n === 1 ? one : many}`;

export interface ProvInfo { label: string; icon: string; hint: string }
export const PROV: Readonly<Record<FieldStatus, ProvInfo>> = Object.freeze({
  proposed: { label: 'Proposed', icon: 'spark', hint: 'Suggested by the analysis. Not confirmed yet.' },
  published: { label: 'Published', icon: 'quote', hint: 'Taken from the published product text. Not confirmed yet.' },
  confirmed: { label: 'Confirmed', icon: 'check', hint: 'You confirmed this value.' },
  edited: { label: 'Edited', icon: 'pencil', hint: 'You set this value yourself.' },
  unresolved: { label: 'Unsure', icon: 'question', hint: 'Marked as unsure. It needs a value before the pack can be exported.' },
});

export const partName = (build: BuildModel, id: string | null): string =>
  build.parts.find((p) => p.id === id)?.label || (id ? id.replace('-', ' ') : 'General');
export const grainText = (p: Pick<Part, 'grainWorld'>): string => (p.grainWorld === 'y' ? 'Vertical' : 'Across');
export const grainArrow = (p: Pick<Part, 'grainWorld'>): string => (p.grainWorld === 'y' ? '↕' : '↔');
export function lineLabel(id: string): string {
  if (id === 'top') return 'Top';
  if (id === 'bottom') return 'Bottom';
  const m = /^shelf-(\d+)(?:-(\d+))?$/.exec(id);
  if (!m) return id;
  if (!m[2]) return `Shelf ${m[1]}`;
  return m[1] === '1' ? `Shelf ${m[2]}` : `Bay ${m[1]} · ${m[2]}`;
}

export const prefersReducedMotion = (): boolean =>
  typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

export function debounce<A extends unknown[]>(fn: (...a: A) => void, ms: number): ((...a: A) => void) & { flush(): void; cancel(): void; pending(): boolean } {
  let t: ReturnType<typeof setTimeout> | null = null;
  let last: A | null = null;
  const run = (...a: A) => {
    last = a;
    if (t) clearTimeout(t);
    t = setTimeout(() => { t = null; const args = last!; last = null; fn(...args); }, ms);
  };
  run.flush = () => { if (t) { clearTimeout(t); t = null; const args = last!; last = null; fn(...args); } };
  run.cancel = () => { if (t) clearTimeout(t); t = null; last = null; };
  run.pending = () => t !== null;
  return run;
}
